import { useNavigate } from 'react-router-dom';
import { DT } from '../../theme/designTokens';
import { useActiveGame } from '../../context/ActiveGameContext';
import { useTranslation } from '../../i18n/react';

/**
 * ActiveGameBanner — the ONE global active-game surface, owned by AppShell.
 * Shows on authenticated non-game screens whenever the user has an active
 * resumable game. Reuses Home's proven rejoin route contract:
 *   navigate(`/game/${game_id}`, { state: { sessionId: session_id } }).
 *
 * Two presentations from authoritative `game_mode`:
 *   • solo bot game  → "BOT GAME SAVED" (calm gold) — available indefinitely.
 *   • multiplayer    → "MATCH IN PROGRESS" (urgent red) — more prominent.
 *
 * Reconnect countdown: NOT shown — the backend does not yet expose a reconnect
 * deadline on active_game (see report). The multiplayer variant uses an urgent
 * non-countdown style instead.
 */
export function ActiveGameBanner() {
  const { activeGame } = useActiveGame();
  const navigate = useNavigate();
  const { t } = useTranslation('play');

  if (!activeGame) return null;

  // game_mode is a semantic enum from the backend — mapped to copy here, at
  // the rendering boundary, never translated as a value.
  const isSolo = activeGame.game_mode === 'bots';
  const label   = isSolo ? t('activeGame.soloLabel')   : t('activeGame.matchLabel');
  const message = isSolo ? t('activeGame.soloMessage') : t('activeGame.matchMessage');
  const action  = isSolo ? t('activeGame.soloAction')  : t('activeGame.matchAction');
  const accent  = isSolo ? DT.color.gold               : DT.color.teamRed;

  const go = () =>
    navigate(`/game/${activeGame.game_id}`, { state: { sessionId: activeGame.session_id } });

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position:       'fixed',
        left:           16,
        right:          16,
        bottom:         16,
        zIndex:         DT.zIndex.toast,
        display:        'flex',
        justifyContent: 'center',
        pointerEvents:  'none', // let the card capture clicks, not the wrapper
      }}
    >
      <div
        style={{
          pointerEvents: 'auto',
          width:         '100%',
          maxWidth:      480,
          boxSizing:     'border-box',
          display:       'flex',
          alignItems:    'center',
          gap:           16,
          padding:       '12px 16px',
          background:    DT.color.cardBgElevated,
          border:        `1px solid ${accent}`,
          borderLeft:    `4px solid ${accent}`,
          borderRadius:  DT.radius.lg,
          boxShadow:     isSolo ? DT.shadow.card : DT.shadow.glowMd,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontFamily:     DT.font.sans,
              fontWeight:     700,
              fontSize:       11,
              letterSpacing:  '0.18em',
              textTransform:  'uppercase',
              color:          accent,
              display:        'flex',
              alignItems:     'center',
              gap:            8,
            }}
          >
            {!isSolo && (
              <span
                aria-hidden
                style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: DT.color.teamRed, flexShrink: 0,
                  boxShadow: `0 0 0 3px rgba(224,82,82,0.25)`,
                }}
              />
            )}
            {label}
          </div>
          <div
            style={{
              fontFamily: DT.font.sans,
              fontSize:   14,
              color:      DT.color.textPrimary,
              marginTop:  2,
              whiteSpace: 'nowrap',
              overflow:   'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {message}
          </div>
        </div>

        <button
          type="button"
          onClick={go}
          style={{
            flexShrink:   0,
            fontFamily:   DT.font.sans,
            fontWeight:   700,
            fontSize:     13,
            padding:      '9px 16px',
            borderRadius: DT.radius.button,
            cursor:       'pointer',
            border:       `1px solid ${isSolo ? DT.color.gold : DT.color.teamRed}`,
            background:   isSolo ? DT.color.gold : DT.color.teamRed,
            color:        isSolo ? DT.color.goldOnGold : '#ffffff',
          }}
        >
          {action}
        </button>
      </div>
    </div>
  );
}
